import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import sorting from '../Utils/sorting';
import helpers from '../Utils/helpers';

export default function CustomerBalanceList({ customerBalance, primaryCustomerOrgNo }) {
    const { t } = useTranslation();
    const [openSubscriptions, setOpenSubscriptions] = useState({});
    const [activeOrgNumber, setActiveOrgNumber] = useState(null);

    if (!customerBalance || !customerBalance.subscriptions || customerBalance.subscriptions.length === 0) {
        return (
            <div className="text-center">
                <p className="m-0">{t('invoicelist.noSubscriptionsFound')}</p>
            </div>
        );
    }

    const toggleSubscription = (id) => {
        setOpenSubscriptions((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const handleOrgRowClick = (orgNumber) => {
        setActiveOrgNumber((prevOrgNumber) => (prevOrgNumber === orgNumber ? null : orgNumber));
    };

    const normalizeOrgNumber = (orgNumber) => {
        return orgNumber ? orgNumber.replace(/-/g, '') : '';
    };

    const renderPeriod = (period) => {
        if (!period || !period.startDate || !period.endDate) return '';
        return `${helpers.formatDate(period.startDate)} - ${helpers.formatDate(period.endDate)}`;
    };

    const normalizedPrimaryCustomerOrgNumber = useMemo(() => {
        return normalizeOrgNumber(primaryCustomerOrgNo);
    }, [primaryCustomerOrgNo]);

    const sortedSubscriptions = useMemo(() => {
        if (!customerBalance?.subscriptions) return [];
        return [...customerBalance.subscriptions].sort(sorting.compareValues('customer.name', 'asc'));
    }, [customerBalance?.subscriptions]);

    const primaryCustomerSubscriptions = useMemo(() => {
        return sortedSubscriptions.filter((s) => normalizeOrgNumber(s.customer.organizationNumber) === normalizedPrimaryCustomerOrgNumber);
    }, [sortedSubscriptions, normalizedPrimaryCustomerOrgNumber]);

    const connectedOrgNumbers = useMemo(() => {
        return [
            ...new Set(
                sortedSubscriptions.map((sub) => normalizeOrgNumber(sub.customer.organizationNumber)).filter((orgNo) => orgNo !== normalizedPrimaryCustomerOrgNumber)
            ),
        ];
    }, [sortedSubscriptions, normalizedPrimaryCustomerOrgNumber]);

    const getSubscriptionsForOrg = (normalizedOrgNo) => {
        if (!sortedSubscriptions || !normalizedOrgNo) return [];
        return sortedSubscriptions.filter((s) => normalizeOrgNumber(s.customer.organizationNumber) === normalizedOrgNo);
    };

    const calculateTotals = (subscriptions) => {
        return subscriptions.reduce(
            (totals, subscription) => {
                subscription.items.forEach((item) => {
                    totals.amount += item.amount || 0;
                    totals.discountAmount += item.discountAmount || 0;
                    totals.totalAmount += item.totalAmount || 0;
                });
                return totals;
            },
            { amount: 0, discountAmount: 0, totalAmount: 0 }
        );
    };

    const renderSubscriptionItems = (subscription, groupKey) => {
        return subscription.items.map((item, index) => {
            const uniqueKey = `${groupKey}-${subscription.id}-${item.name}-${index}`;

            return (
                <tr key={uniqueKey}>
                    <td width={350}></td>
                    <td width={300}>{item.name}</td>
                    <td width={300}>{renderPeriod(item.period)}</td>
                    <td className="text-right">{item.quantity}</td>
                    <td className="text-right">{item.unitPrice}</td>
                    <td className="text-right">{item.amount}</td>
                    <td className="text-right">{item.discountAmount}</td>
                    <td className="text-right">{item.totalAmount}</td>
                </tr>
            );
        });
    };

    const renderSubscriptionHeader = (subscription, isUnderOrgGroup = false) => {
        const isExpanded = openSubscriptions[subscription.id];
        const totals = calculateTotals([subscription]);

        return (
            <tr key={`header-${subscription.id}`} onClick={() => toggleSubscription(subscription.id)}>
                <td colSpan="6" className={`${isExpanded ? 'bold' : ''} ${isUnderOrgGroup ? 'pl-32' : ''}`}>
                    <span className={`caret caret-lg ${isExpanded ? 'open' : ''}`} />
                    <span className="ml-24 d-inline-block">{subscription.name}</span>
                </td>
                <td className={`text-right ${isExpanded ? 'bold' : ''}`}>{totals.discountAmount}</td>
                <td className={`text-right ${isExpanded ? 'bold' : ''}`}>{totals.totalAmount}</td>
            </tr>
        );
    };

    const renderSubscriptionsForOrg = (subscriptions, groupKey, isUnderOrgGroup = false) => {
        return subscriptions.map((subscription) => {
            const isExpanded = openSubscriptions[subscription.id];

            return (
                <React.Fragment key={`${groupKey}-${subscription.id}`}>
                    {renderSubscriptionHeader(subscription, groupKey, isUnderOrgGroup)}
                    {isExpanded && renderSubscriptionItems(subscription, groupKey)}
                </React.Fragment>
            );
        });
    };

    const renderPrimaryCustomerSection = () => {
        if (primaryCustomerSubscriptions.length === 0) return null;

        return renderSubscriptionsForOrg(primaryCustomerSubscriptions, normalizedPrimaryCustomerOrgNumber, false);
    };

    const renderSeparatorRow = () => {
        if (connectedOrgNumbers.length === 0 || primaryCustomerSubscriptions.length === 0) return null;

        return (
            <tr className="non-interactive-row">
                <td colSpan={8} className="p-0" style={{ height: '40px' }}>
                    <hr className="m-0" style={{ borderColor: '#DADADA' }}></hr>
                </td>
            </tr>
        );
    };

    const renderConnectedOrganizations = () => {
        return connectedOrgNumbers.map((normalizedOrgNumber) => {
            const currentSubscriptions = getSubscriptionsForOrg(normalizedOrgNumber);
            const customer = currentSubscriptions[0].customer;
            const totals = calculateTotals(currentSubscriptions);
            const isExpanded = activeOrgNumber === normalizedOrgNumber;

            return (
                <React.Fragment key={normalizedOrgNumber}>
                    <tr onClick={() => handleOrgRowClick(normalizedOrgNumber)}>
                        <td colSpan={6} className="bold">
                            <span className={`caret caret-lg ${isExpanded ? 'open' : ''}`}></span>
                            <span className="ml-24 d-inline-block">{customer.organizationNumber}</span>
                            <span className="ml-16 d-inline-block">{customer.name}</span>
                        </td>
                        <td className="bold text-right">{totals.discountAmount}</td>
                        <td className="bold text-right">{totals.totalAmount}</td>
                    </tr>
                    {isExpanded && renderSubscriptionsForOrg(currentSubscriptions, normalizedOrgNumber, true)}
                </React.Fragment>
            );
        });
    };

    return (
        <table className="table table-active table-stripe">
            <thead>
                <tr>
                    <th width={350}>{t('invoicelist.package')}</th>
                    <th width={300}>{t('invoicelist.name')}</th>
                    <th width={300}>{t('invoicelist.period')}</th>
                    <th className="text-right">{t('invoicelist.quantity')}</th>
                    <th className="text-right">{t('invoicelist.unitprice')}</th>
                    <th className="text-right">{t('invoicelist.amount')}</th>
                    <th className="text-right">{t('invoicelist.discountamount')}</th>
                    <th className="text-right">{t('invoicelist.totalamount')}</th>
                </tr>
            </thead>
            <tbody>
                {renderPrimaryCustomerSection()}
                {renderSeparatorRow()}
                {renderConnectedOrganizations()}
            </tbody>
            <tfoot>
                <tr>
                    <td colSpan={6}></td>
                    <td className="bold text-right">{customerBalance.totalDiscountAmount}</td>
                    <td className="bold text-right">{customerBalance.totalAmount}</td>
                </tr>
            </tfoot>
        </table>
    );
}
